import { buildGroupHierarchy, findGroupCode, getGroupCode, measurementUnits, statusList } from "../../../../Utils/Utils";
import CommonToolbar from "../../../UIElements/CommonToolbar";
import EnumsContext from "../../../../context/enums-context";
import HistoryElement from "../../../UIElements/HistoryElement";
import useApi from "../../../../Utils/BackendClient";
import { useFormik } from "formik";
import { useIsMobile } from "../../../../Utils/ResizeUtils";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useContext, useEffect, useRef, useMemo } from "react";
import useGetEnums from "../../../../Utils/EnumsUtils";

import { Button } from "primereact/button";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { TabMenu } from "primereact/tabmenu";
import { Toast } from "primereact/toast";
import { TreeSelect } from "primereact/treeselect";

import './ProductCreation.module.css';
import commonStyle from "../../CommonStyles.module.css";
import BrandCreationSidebar from "../../../UIElements/Sidebars/BrandCreationSidebar";

import basicLogo from "../../../../images/baseThumbnail.png";
import { Carousel } from "primereact/carousel";
import { DisplayErrors } from "../../../../Utils/ErrorHandler";
import { Tooltip } from "primereact/tooltip";
import { ProgressBar } from "primereact/progressbar";
import { Tag } from "primereact/tag";
import ProductGroupCreationSidebar from "../../../UIElements/Sidebars/ProductGroupCreationSidebar";
import TextInput from "../../../UIElements/Inputs/TextInput";
import CurrencyAmountInput from "../../../UIElements/Inputs/CurrencyAmountInput";


const ProductCreation = () => {

    const isMobile = useIsMobile();
    const fileUploadRef = useRef(null);
    const toast = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const ctx = useContext(EnumsContext);
    
    const { createProduct, deleteProductImages, fetchCustomDictionaries, fetchProductsExtended, fetchProductDetails, fetchProductGroupDetails, 
        fetchProductHistory, fetchProductImages, setImageAsProductLogo, updateProduct, uploadProductLogo } = useApi();

    const [sortedGroups, setSortedGroups] = useState([]);
    const [productHistory, setProductHistory] = useState([]);
    const [productsImages, setProductsImages] = useState([{
        "id": 0,
        "image_url": basicLogo
    }]);
    const [historyTotalRecords, setHistoryTotalRecords] = useState(0);
    const [dictionaries, setDictionaries] = useState(null); 
    const [isEdit, setIsEdit] = useState(false);
    const [tabShowed, setTabShowed] = useState(0);
    const [product, setProduct] = useState({
        product_name: "",
        product_group: null,
        product_article: "",
        currency: "UAH",
        measurement_unit: "PIECES",
        brand_id: null,
        accounting_type: null,
        price: "",
        is_active: "Активний",
        notes: "",
        attributes_values: {}
    });
    const [historyLazyState, setHistoryLazyState] = useState({
        first: 0,
        rows: 10,
        page: 1   
    });
    const [submitLoading, setSubmitLoading] = useState(false);
    const [totalSize, setTotalSize] = useState(0);
    const [visible, setVisible] = useState(false);
    const [groupCreationVisible, setGroupCreationVisible] = useState(false);
    const [reloadData, setReloadData] = useState(true);

    const enumTypes = useMemo(() => ["product_groups", "brands_dictionary"], []);

    useGetEnums(enumTypes);

    useEffect(() => {

        const getGroupsExtended = async () => {

            const response = await fetchProductsExtended();

            if (response.status === 200) {

                const groups = buildGroupHierarchy(response.data);
                setSortedGroups(groups);

                if (location.state.id !== undefined) {
                    setIsEdit(true);

                    const fetchData = async () => {

                        const response = await fetchProductDetails(location.state.id);

                        if (response.status === 200) {
                            response.data.product_group = findGroupCode(groups, response.data.product_group);
                            response.data.is_active = response.data.is_active ? "Активний" : "Деактивований";

                            const dictionaryIds = response.data.attributes_values
                                .filter(item => item.type === "DICTIONARY" && item.dictionary_id != null)
                                .map(item => item.dictionary_id);

                            if(dictionaryIds && dictionaryIds.length > 0){
                                const getDictionaries = async () => {

                                    const response = await fetchCustomDictionaries({
                                        first: 0,
                                        rows: 10,
                                        page: 1,
                                        sortField: null,
                                        sortOrder: null,
                                    }, dictionaryIds)

                                    if (response.status === 200) {
                                        const transformed = response.data.results.reduce((acc, dictionary) => {
                                            acc[dictionary.id] = dictionary.items.map(item => ({
                                                value: item.id,
                                                label: item.value
                                            }));
                                            return acc;
                                        }, {});

                                        setDictionaries(transformed);
                                    }
                                }

                                getDictionaries();
                            }

                            setProduct(response.data);
                        }
                    };

                    fetchData();
                }
            }
        }

        getGroupsExtended();

    }, [location.state, fetchCustomDictionaries, fetchProductDetails, fetchProductsExtended, reloadData]);

    const formik = useFormik({
        initialValues: product,
        enableReinitialize: true,
        validate: values => {
            const errors = {};

            if (!values.product_name) {
                errors.product_name = "Назва продукту не повинна бути пустою";
            }

            if (!values.product_group) {
                errors.product_group = "Назва групи не повинна бути пустою";
            }

            if (!values.price) {
                errors.price = "Введіть ціну товару";
            }

            if (values.attributes_values?.length) {
                const attributesErrors = [];

                values.attributes_values.forEach((attribute, index) => {
                    const attrError = {};

                    if (attribute.is_required) {
                        if (attribute.type === "DICTIONARY" && !attribute.dictionary_item_id) {
                            attrError.dictionary_item_id = "Виберіть значення з довідника";
                        }

                        if (attribute.type !== "DICTIONARY" && !attribute.value) {
                            attrError.value = "Значення не повинне бути пустим";
                        }
                    }

                    if (Object.keys(attrError).length > 0) {
                        attributesErrors[index] = attrError;
                    }
                });

                if (attributesErrors.length > 0) {
                    errors.attributes_values = attributesErrors;
                }
            }

            return errors;
        },
        onSubmit: async (values) => {

            setSubmitLoading(true);

            const payload = {
                ...values,
                product_group: getGroupCode(values.product_group),
                is_active: values.is_active === "Активний",
            };

            const response = location.state.id === undefined ? await createProduct(payload) : await updateProduct(location.state.id, payload);

            setSubmitLoading(false);

            const successStatus = isEdit ? 200 : 201;

            if (response.status === successStatus) {
                 let toastMessage = location.state.id === undefined ? { severity: "success", summary: "Успіх створення", detail: "Товар " + values.product_name + " успішно створено", life: 3000 } :
                    { severity: "success", summary: "Успіх редагування", detail: "Товар " + values.product_name + " успішно відредаговано", life: 3000 }

                if (!isEdit) {
                    if (fileUploadRef?.current?.getFiles()) {
                        await onTemplateUpload(fileUploadRef.current.getFiles(), response.data.id);
                    }
                }

                navigate("/products", { state: { toast: toastMessage, productPageState: location.state.productPageState } });
            }
            else{
                DisplayErrors(response.error, toast);
            }
        }
    });

    const DropdownChangeHandler = async e => {
        let product_group_code = e.value;

        let productGroupId = product_group_code;

        if (product_group_code !== null && product_group_code !== undefined) {
            if (product_group_code.toString().includes('-')) {
                var keys = product_group_code.split('-');
                productGroupId = keys[(keys.length - 1)];
            }
        }
        else {
            product_group_code = null;
        }

        formik.setFieldValue('product_group', product_group_code);

        if (productGroupId) {

            const fetchData = async () => {

                const response = await fetchProductGroupDetails(productGroupId);

                if (response.status === 200) {

                    const attributes = response.data.attributes;

                    console.log(response.data);
                    
                    formik.setFieldValue('accounting_type', response.data.accounting_type);
                    if (response.data.accounting_type === "INDIVIDUAL") {
                        formik.setFieldValue('measurement_unit', "PIECES");
                    }
                    formik.setFieldValue('attributes_values', attributes);

                    const dictionaryIds = response.data.attributes
                        .filter(item => item.type === "DICTIONARY" && item.dictionary_id != null)
                        .map(item => item.dictionary_id);

                    if (dictionaryIds && dictionaryIds.length > 0) {
                        const response = await fetchCustomDictionaries({
                            first: 0,
                            rows: 10,
                            page: 1,
                            sortField: null,
                            sortOrder: null,
                        }, dictionaryIds)

                        if (response.status === 200) {
                            const transformed = response.data.results.reduce((acc, dictionary) => {
                                acc[dictionary.id] = dictionary.items.map(item => ({
                                    value: item.id,
                                    label: item.value
                                }));
                                return acc;
                            }, {});

                            setDictionaries(transformed);
                        }
                    }
                }
            };

            fetchData();
        }
    };

    const getProductHistory = async (id, state) => {
        
        const response = await fetchProductHistory(id, state);

        if(response.status === 200){
            setProductHistory(response.data.results);
            setHistoryTotalRecords(response.data.count);
        }
        else{
            toast.current.show({ severity: "error", summary: "Помилка отримання історії", detail: "Помилка сервера " + response.status, life: 3000 });
        }
    }

    const getProductLogo = async(id) => {

        const response = await fetchProductImages(id);

        if(response.status === 200)
        {
            setProductsImages(response.data.results);
            console.log(response.data);
        }
        else{
            DisplayErrors(response.error, toast);
        }
    }

    const onHistoryPage = (event) => {
        event.page = event.page + 1;
        getProductHistory(formik.values?.id, event);

        setHistoryLazyState(event);
    }

    const items = [
        {
            label: 'Налаштуванна полів',
            icon: 'pi pi-book',
            command: () => {
                setTabShowed(0);
            },
        },
        {
            label: 'Опис',
            icon: 'pi pi-comment',
            command: () => {
                setTabShowed(1);
            },
        },
        {
            label: 'Фото товару',
            icon: 'pi pi-image',
            command: () => {
                if(isEdit){
                    getProductLogo(formik.values?.id);
                }

                setTabShowed(2);
            },
        },
        ...(isEdit
            ? [
                {
                    label: 'Історія',
                    icon: 'pi pi-history',
                    command: () => {
                        setTabShowed(3);
                        getProductHistory(formik.values?.id, historyLazyState);
                    },
                },
            ]
            : []),
    ];

    const toolbarLeftData = () => {

        let name = "Створення товару";

        if (isEdit) {
            name = "Редагування товару " + formik.values.product_name;
        }
        return !isMobile ? <h3>{name}</h3> : null;
    };

    const confirmLeave = (event) => {

        if (formik.values !== product) {

            confirmDialog({
                group: 'headless',
                header: 'Незбережені зміни',
                message: 'У вас є незбережені зміни. Ви впевнені, що хочете залишити цю сторінку?',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: "Так",
                rejectLabel: "Hi",
                defaultFocus: 'accept',
                accept: () => { navigate('/products', { state: { productPageState: location.state.productPageState } })}
            });

        }
        else {
            navigate('/products', { state: { productPageState: location.state.productPageState } });
        }
    }

    const assignLogoHandler = async (id) => {
        const request = {
            image_id: id,
            product_id: formik.values?.id
        }

        const response = await setImageAsProductLogo(request);

        if(response.status === 200){
            toast.current.show({ severity: "success", summary: "Успіх вибору лого", detail: "Фото успішно додано як лого до товару", life: 3000 });

            getProductLogo(formik.values?.id);
        }
        else{
            DisplayErrors(response.error, toast);
        }
    }

    const deleteImageHandler = async (id) => {
        const request = {
            images_ids: [id],
            product_id: formik.values?.id
        }

        console.log(productsImages);

        const response = await deleteProductImages(request);

        if (response.status === 204) {
            toast.current.show({ severity: "success", summary: "Успіх видалення", detail: "Фото успішно видалено", life: 3000 });

            getProductLogo(formik.values?.id);
        }
        else {
            DisplayErrors(response.error, toast);
        }
    }

    const productTemplate = (product) => {
        console.log(product);
        return (<>
            {!isEdit &&
                <div className={commonStyle.previewImageWrapper}>
                    <img className={commonStyle.productImage} alt={product.id} src={product.image_url} style={{ borderRadius: "2%" }} />
                </div>}
            {isEdit &&
                <div className="text-center py-2 px-1">
                    <div className={commonStyle.previewImageWrapper}>
                        <img alt={product.id} src={product.image_url} height={200} width={150} className={commonStyle.productImage} />
                    </div>
                    <div className="mt-5 flex flex-wrap gap-2 justify-content-center">
                        <Button
                            icon="pi pi-star-fill"
                            className="p-button p-button-rounded"
                            outlined={!product.is_logo}
                            type="button"
                            disabled={product.is_logo}
                            tooltip={product.is_logo ? "Лого" : "Встановити лого"}
                            tooltipOptions={{ position: "top", showOnDisabled: true }}
                            onClick={() => assignLogoHandler(product.id)}
                        />
                        <Button
                            icon="pi pi-trash"
                            className="p-button-danger p-button-rounded"
                            type="button"
                            tooltip="Видалити"
                            tooltipOptions={{ position: "top" }}
                            onClick={() => deleteImageHandler(product.id)}
                        />
                    </div>
                </div>}
        </>);
    };

    const onTemplateSelect = (e) => {
        let _totalSize = totalSize;
        let files = e.files;

        Object.keys(files).forEach((key) => {
            _totalSize += files[key].size || 0;
        });

        console.log(e.files);
        setTotalSize(_totalSize);
    };

    const onTemplateUpload = async (e, id = null) => {

        console.log(e);

        const files = isEdit ? e.files : e;
        const formData = new FormData();

        files.forEach((file) => {
            formData.append("uploaded_images", file, file.name);
        });

        formData.append('product_id', id ?? formik.values?.id);

        const response = await uploadProductLogo(formData);

        if (response.status === 201) {

            if (isEdit) {
                getProductLogo(formik.values?.id);
                fileUploadRef.current.clear();
                onTemplateClear();
            }

            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Фото завантажено' });
        }
        else {
            DisplayErrors(response.error, toast);
        }
    };

    const onTemplateRemove = (file, callback) => {
        setTotalSize(totalSize - file.size);
        callback();
    };

    const onTemplateClear = () => {
        setTotalSize(0);
    };

    const createProductGroupHandler = (id) => {
        setReloadData(!reloadData);

        formik.setFieldValue("product_group", id);

    }

    const headerTemplate = (options) => {
        const { className, chooseButton, uploadButton, cancelButton } = options;
        const value = totalSize / 100000;
        const formatedValue = fileUploadRef && fileUploadRef.current ? fileUploadRef.current.formatSize(totalSize) : '0 B';

        return (
            <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center' }}>
                {chooseButton}
                {uploadButton}
                {cancelButton}
                <div className="flex align-items-center gap-3 ml-auto">
                    <span>{formatedValue} / 10 MB</span>
                    <ProgressBar value={value} showValue={false} style={{ width: '10rem', height: '12px' }}></ProgressBar>
                </div>
            </div>
        );
    };

    const itemTemplate = (file, props) => {
        return (
            <div className="flex align-items-center flex-wrap">
                <div className="flex align-items-center" style={{ width: '40%' }}>
                    <img alt={file.name} role="presentation" src={file.objectURL} width={100} />
                    <span className="flex flex-column text-left ml-3">
                        {file.name}
                        <small>{new Date().toLocaleDateString()}</small>
                    </span>
                </div>
                <Tag value={props.formatSize} severity="warning" className="px-3 py-2" />
                <Button type="button" icon="pi pi-times" className="p-button-outlined p-button-rounded p-button-danger ml-auto" onClick={() => onTemplateRemove(file, props.onRemove)} />
            </div>
        );
    };

    const emptyTemplate = () => {
        return (
            <div className="flex align-items-center flex-column">
                <i className="pi pi-image mt-3 p-5" style={{ fontSize: '5em', borderRadius: '50%', backgroundColor: 'var(--surface-b)', color: 'var(--surface-d)' }}></i>
                <span style={{ fontSize: '1.2em', color: 'var(--text-color-secondary)' }} className="my-5">
                    {"Додайте або перетягніть картинку сюди (максимум 3)"} <br/>
                    {!isEdit && "Фото буде збережене разом зі збереженням товару"}
                </span>
            </div>
        );
    };

    const chooseOptions = { icon: 'pi pi-fw pi-images', iconOnly: true, className: 'custom-choose-btn p-button-rounded p-button-outlined' };
    const uploadOptions = { icon: 'pi pi-fw pi-cloud-upload', iconOnly: true, className: 'custom-upload-btn p-button-success p-button-rounded p-button-outlined' };
    const cancelOptions = { icon: 'pi pi-fw pi-times', iconOnly: true, className: 'custom-cancel-btn p-button-danger p-button-rounded p-button-outlined' };

    console.log(formik.errors);

    return (
        <form onSubmit={formik.handleSubmit}>
            <Toast ref={toast}/>
            <CommonToolbar isMobile={isMobile} isFinalized={false} loading={submitLoading} onRetunButtonClick={(event) => confirmLeave(event)} leftContent={toolbarLeftData()}/>
            <ConfirmDialog group="headless" />
            <div className="grid nested-grid">
                <div className="leftMenu col-3">
                    <div className={commonStyle.formInput}>
                        <div className="p-inputgroup flex-column">
                            <FloatLabel>
                                <TreeSelect
                                    name="product_group"
                                    value={formik.values.product_group}
                                    onChange={DropdownChangeHandler}
                                    options={sortedGroups}
                                    showClear
                                    className={formik.errors.product_group && formik.touched.product_group ? 'p-invalid w-full' : 'w-full'} />
                                <Button
                                    icon="pi pi-plus"
                                    className={commonStyle.addButton}
                                    severity="success"
                                    tooltip="Додати групу"
                                    type="button"
                                    onClick={() => setGroupCreationVisible(true)}
                                />
                                <label>Назва групи{<span className={commonStyle.requiredAsterisk}>*</span>}</label>
                            </FloatLabel>
                            {formik.errors.product_group && formik.touched.product_group && (<small className={commonStyle.errorSmall}>{formik.errors.product_group}</small>)}
                        </div>
                    </div>
                    <TextInput formik={formik} paramName={"product_name"} label={"Назва товару"} mandatory />
                    <TextInput formik={formik} paramName={"product_article"} label={"Артикул"} />
                    <div className={commonStyle.formInput}>
                        <div className="p-inputgroup flex-column">
                            <FloatLabel>
                                <Dropdown name="measurement_unit"
                                    value={formik.values.measurement_unit}
                                    onChange={e => formik.setFieldValue('measurement_unit', e.value)}
                                    disabled={formik.values.accounting_type === "INDIVIDUAL"}
                                    options={measurementUnits}
                                    className="w-full" />
                                <label>Одиниця виміру</label>
                            </FloatLabel>
                        </div>
                    </div>
                    <div className={commonStyle.formInput}>
                        <div className="p-inputgroup flex-column">
                            <FloatLabel>
                                <Dropdown name="brand"
                                    value={formik.values.brand_id}
                                    onChange={e => formik.setFieldValue('brand_id', e.value)}
                                    options={ctx.enums.brands_dictionary.data}
                                    className="w-full" 
                                />
                                <Button
                                    icon="pi pi-plus"
                                    className={commonStyle.addButton}
                                    severity="success"
                                    tooltip="Додати бренд"
                                    type="button"
                                    onClick={() => setVisible(true)}
                                />
                                <label>Бренд</label>
                            </FloatLabel>
                        </div>
                    </div>
                    <CurrencyAmountInput
                        amount={formik.values.price}
                        currency={formik.values.currency}
                        onAmountChange={(val) => formik.setFieldValue("price", val)}
                        onCurrencyChange={(val) => formik.setFieldValue("currency", val)}
                        errors={formik.errors.price}
                        touched={formik.touched.price}
                        mandatory
                    />
                    <div className={commonStyle.formInput}>
                        <div className="p-inputgroup flex-1">
                            <FloatLabel>
                                <Dropdown name="status" value={formik.values.is_active} onChange={e => formik.setFieldValue('is_active', e.value)} options={statusList} showClear className="w-full" />
                                <label>Статус</label>
                            </FloatLabel>
                        </div>
                    </div>
                </div>
                <div className="col-9">
                    <TabMenu model={items} />
                    {tabShowed === 0 &&
                        <div className="grid">
                            {formik.values.attributes_values.length > 0 && (formik.values.attributes_values.map((field, index) => (
                                <div className="col-4" key={field.attribute_id}>
                                    <div className={`${commonStyle.inputfields} flex-column p-inputgroup`}>
                                        {field.type !== "DICTIONARY" && <FloatLabel>
                                            <InputText
                                                name={`attributes_values.${index}.value`}
                                                value={field.value}
                                                onChange={(e) =>
                                                    formik.setFieldValue(`attributes_values.${index}.value`, e.target.value)
                                                }
                                                onBlur={formik.handleBlur}
                                                className={formik.touched.attributes_values?.[index]?.value && formik.errors.attributes_values?.[index]?.value ? "p-invalid" : ""}
                                            />
                                            <label>{field.name}{field.is_required && <span className={commonStyle.requiredAsterisk}>*</span>}</label>
                                        </FloatLabel>}
                                        {field.type === "DICTIONARY" && <FloatLabel>
                                            <Dropdown
                                                name={`attributes_values.${index}.dictionary_item_id`}
                                                value={field.dictionary_item_id}
                                                options={dictionaries?.[field.dictionary_id] ?? []}
                                                onChange={(e) =>
                                                    formik.setFieldValue(
                                                        `attributes_values.${index}.dictionary_item_id`,
                                                        e.value
                                                    )
                                                }
                                                onBlur={formik.handleBlur}
                                                className={formik.touched.attributes_values?.[index]?.dictionary_item_id && formik.errors.attributes_values?.[index]?.dictionary_item_id ? "p-invalid" : ""}
                                                showClear
                                            />
                                            <label>{field.name}{field.is_required && <span className={commonStyle.requiredAsterisk}>*</span>}</label>
                                        </FloatLabel>}
                                        {formik.touched.attributes_values?.[index]?.value &&
                                            formik.errors.attributes_values?.[index]?.value && (
                                                <small className={commonStyle.errorSmall}>
                                                    {formik.errors.attributes_values[index].value}
                                                </small>
                                            )}
                                        {formik.touched.attributes_values?.[index]?.dictionary_item_id &&
                                            formik.errors.attributes_values?.[index]?.dictionary_item_id && (
                                                <small className={commonStyle.errorSmall}>
                                                    {formik.errors.attributes_values[index].dictionary_item_id}
                                                </small>
                                            )}
                                    </div>
                                </div>
                            )))}
                        </div>}
                    {tabShowed === 1 && <div className="tableStyle">
                        <InputTextarea name="notes" value={formik.values.notes} onChange={e => formik.setFieldValue('notes', e.target.value)} className={commonStyle.fullWidth} rows={10} />
                    </div>}
                    {tabShowed === 2 && <div className="grid mt-5">
                        <div className="col-3">
                            <div className={commonStyle.portraitdiv}>
                                <Carousel value={productsImages} numVisible={1} numScroll={1} orientation="vertical" verticalViewPortHeight="300px" itemTemplate={productTemplate} />
                            </div>
                        </div>
                        <div className="col-9">

                            <Tooltip target=".custom-choose-btn" content="Вибрати" position="bottom" />
                            <Tooltip target=".custom-upload-btn" content="Завантажити" position="bottom" />
                            <Tooltip target=".custom-cancel-btn" content="Очистити" position="bottom" />

                            <FileUpload ref={fileUploadRef} accept="image/*" multiple maxFileSize={10000000} customUpload
                                uploadHandler={onTemplateUpload} onSelect={onTemplateSelect} onError={onTemplateClear} onClear={onTemplateClear}
                                headerTemplate={headerTemplate} itemTemplate={itemTemplate} emptyTemplate={emptyTemplate}
                                chooseOptions={chooseOptions} uploadOptions={uploadOptions} cancelOptions={cancelOptions} />
                        </div>

                    </div>}
                    {tabShowed === 3 && <HistoryElement data={productHistory} lazyState={historyLazyState} totalRecords={historyTotalRecords} onHistoryPage={onHistoryPage} isAct={false}/>}
                </div>
            </div>
            <BrandCreationSidebar visible={visible} onHide={() => setVisible(false)} toast={toast} ctx={ctx} additionalActions={(id) => formik.setFieldValue("brand_id", id)}/>
            <ProductGroupCreationSidebar visible={groupCreationVisible} onHide={() => setGroupCreationVisible(false)} toast={toast} groups={sortedGroups} onCreate={(id) => createProductGroupHandler(id)} componentKey={location.pathname}/>
        </form>
    );
}

export default ProductCreation;